import cx from 'classnames'
import { useState, Fragment } from 'react'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { isNil } from 'lodash'

import useGlobalConfirmBox from 'features/GlobalConfirmBox/hooks/useGlobalConfirmBox'
import { ROUTE_PATH } from 'lib/paths'
import { Group } from 'lib/_types'
import { getServerErrorMessage } from 'lib/error'
import { setRedirectUrl } from 'lib/router'
import { useCheckAuthorizeBeforeAction } from 'hooks'
import { togglePostBoxModal } from 'store/actions'
import { useRemoteConfig } from 'store/slices/remoteConfig'
import { addNotification } from 'store/slices/notifications'
import { setPostBoxShareToTimeline } from 'store/slices/postBox'
import { useNavigate } from 'features/i18n/router'
import {
  groupToGroupContextSchema,
  logEvent,
  snowplowEvent,
} from 'lib/analytics'
import {
  useDeleteGroupMutation,
  useJoinGroupMutation,
  useLeaveGroupMutation,
} from 'store/api/yay/group'

import ConfirmBox from 'components/ConfirmBox'
import { useCurrentUser } from 'store/api/yay/user'

const CONFIRM_BOX_MODE = {
  joinPrivateCancel: 1,
  joinPrivate: 2,
  accessPrivateFromPublic: 3,
  leaveGroup: 4,
  deleteGroup: 5,
  groupGuidelines: 6,
}

export const useGroupActions = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const navigate = useNavigate()

  const currentUserId = useCurrentUser()?.data?.id
  const { is_web_phone_verification_enabled } = useRemoteConfig()
  const { showConfirmBox } = useGlobalConfirmBox()
  const checkAuthorizeBeforeAction = useCheckAuthorizeBeforeAction()

  const [joinGroup] = useJoinGroupMutation()
  const [leaveGroup] = useLeaveGroupMutation()
  const [deleteGroup] = useDeleteGroupMutation()

  const [__selectedGroup, __selectedGroupSet] = useState<{
    group: Group
    confirmBoxMode: number
    callback?: Function
  }>()

  const closeModal = () => __selectedGroupSet(null)

  const onJoinGroup = async (group: Partial<Group> = {}) => {
    if (!group.id) return

    const joinGroupRes = await joinGroup({
      groupId: group.id,
    })

    if ((joinGroupRes as any)?.error?.data?.error_code === -405) {
      if (is_web_phone_verification_enabled) {
        showConfirmBox({
          title: t(
            'with_group_action.confirm_box.join_group_require_phone_verification.title'
          ),
          content: t(
            'with_group_action.confirm_box.join_group_require_phone_verification.content'
          ),
          confirmBoxProps: {
            onOk: () => navigate(setRedirectUrl(ROUTE_PATH.SETTING_PHONE)),
            cancelText: t('common.not_now'),
          },
        })
      } else {
        dispatch(
          addNotification({
            id: 'verify phone number',
            message: getServerErrorMessage(-405),
            type: 'error',
          })
        )
      }
    }

    logEvent.group_join({
      target_group_id: group.id.toString(),
      target_group_type: group.is_private ? 'private' : 'public',
    })

    const context = {
      group: groupToGroupContextSchema({
        group: group,
        currentUserId,
      }),
    }

    if (group.is_private) {
      snowplowEvent.request_join_group(context)
    } else {
      snowplowEvent.join_group(context)
    }

    return joinGroupRes
  }

  const onLeaveGroup = async (group: Group) => {
    if (!group) return

    leaveGroup({
      groupId: group.id,
    }).then((res) => {
      if ((res as any)?.data?.result == 'success') {
        closeModal()

        logEvent.group_leave({
          group_id: group.id,
          group_category_id: group.group_category_id,
        })

        // In `<PostShareableGroup />`, we don't reload, though.
        if (isNil(__selectedGroup?.callback)) {
          dispatch(
            addNotification({
              message: t('alert_group_left_title'),
            })
          )

          // You can leave group in ROUTE_PATH.GROUPS page, but it's
          // an infinity loader there, so there is no easy way to reload it.
          window?.location?.reload?.()
        }

        return res
      }
    })
  }

  const onDeleteGroup = async (group?: Group) => {
    if (!group) return

    deleteGroup({
      groupId: group.id,
    }).then((res) => {
      if ((res as any)?.data?.result == 'success') {
        closeModal()

        dispatch(
          addNotification({
            message: t('notification.delete_group_success'),
          })
        )

        logEvent.group_delete({
          group_id: group.id,
          group_category_id: group.group_category_id,
        })

        window.location.href = ROUTE_PATH.GROUPS
      }
    })
  }

  return {
    shareGroupToTimeline: (groupId: string) => {
      dispatch(setPostBoxShareToTimeline({ type: 'group' }))

      dispatch(
        togglePostBoxModal({
          groupId,
        })
      )
    },

    onJoinGroup,
    onLeaveGroup,

    attemptJoinGroup: checkAuthorizeBeforeAction(
      (group: Group, callback?: Function) => {
        if (group?.is_joined) {
          return onLeaveGroup(group)
        } else {
          if (group?.is_private && !group?.invited_to_join) {
            if (group?.is_pending) {
              __selectedGroupSet({
                group,
                confirmBoxMode: CONFIRM_BOX_MODE.joinPrivateCancel,
                callback,
              })
            } else if (group?.guidelines?.length > 0) {
              __selectedGroupSet({
                group,
                confirmBoxMode: CONFIRM_BOX_MODE.groupGuidelines,
                callback,
              })
            } else {
              __selectedGroupSet({
                group,
                confirmBoxMode: CONFIRM_BOX_MODE.joinPrivate,
                callback,
              })
            }
          } else if (group?.guidelines?.length > 0) {
            __selectedGroupSet({
              group,
              confirmBoxMode: CONFIRM_BOX_MODE.groupGuidelines,
              callback,
            })
          } else {
            return onJoinGroup(group)
          }
        }
      }
    ),

    attemptLeaveGroup: (group: Group) => {
      __selectedGroupSet({
        group,
        confirmBoxMode: CONFIRM_BOX_MODE.leaveGroup,
      })
    },

    attemptDeleteGroup: (group: Group) => {
      __selectedGroupSet({
        group,
        confirmBoxMode: CONFIRM_BOX_MODE.deleteGroup,
      })
    },

    GroupActionConfirmBox: () => {
      if (isNil(__selectedGroup)) {
        return null
      }

      return (
        <ConfirmBox isActive>
          <a className="ConfirmBox__overlay" onClick={closeModal} />

          <div
            className={cx('ConfirmBox__wrapper', {
              'ConfirmBox__wrapper--group-guidelines':
                __selectedGroup?.confirmBoxMode ==
                CONFIRM_BOX_MODE.groupGuidelines,
            })}
          >
            <div className="ConfirmBox__body">
              <h3 className="ConfirmBox__body__h3">
                {(() => {
                  const groupName =
                    __selectedGroup?.group?.topic ?? t('common.group')

                  switch (__selectedGroup?.confirmBoxMode) {
                    case CONFIRM_BOX_MODE.leaveGroup:
                      return t(
                        'with_group_action.confirm_box.leave_group.title',
                        { groupName }
                      )
                    case CONFIRM_BOX_MODE.deleteGroup: {
                      return t(
                        'with_group_action.confirm_box.delete_group.title',
                        { groupName }
                      )
                    }
                    case CONFIRM_BOX_MODE.groupGuidelines: {
                      return t('create_group_modal.guidelines.label')
                    }
                    default: {
                      return t('common.private_circle')
                    }
                  }
                })()}
              </h3>

              {(() => {
                switch (__selectedGroup?.confirmBoxMode) {
                  case CONFIRM_BOX_MODE.joinPrivate:
                  case CONFIRM_BOX_MODE.joinPrivateCancel: {
                    return (
                      <p className="ConfirmBox__body__p">
                        <span
                          dangerouslySetInnerHTML={{
                            __html: t(
                              'with_group_action.confirm_box.join_private_channel.content'
                            ),
                          }}
                        />
                      </p>
                    )
                  }
                  case CONFIRM_BOX_MODE.accessPrivateFromPublic: {
                    return (
                      <p className="ConfirmBox__body__p">
                        {t(
                          'with_group_action.confirm_box.access_from_public.content'
                        )}
                      </p>
                    )
                  }
                  case CONFIRM_BOX_MODE.groupGuidelines: {
                    return (
                      <p className="ConfirmBox__body__p ConfirmBox__body__p--guidelines">
                        {__selectedGroup?.group?.guidelines}
                      </p>
                    )
                  }
                }
              })()}
            </div>

            <div
              className={cx(
                'ConfirmBox__footer',
                __selectedGroup?.confirmBoxMode ==
                  CONFIRM_BOX_MODE.groupGuidelines
                  ? {
                      'ConfirmBox__footer--jc-fe': true,
                    }
                  : {
                      'ConfirmBox__footer--fluid': true,
                    }
              )}
            >
              {(() => {
                switch (__selectedGroup?.confirmBoxMode) {
                  case CONFIRM_BOX_MODE.joinPrivate: {
                    return (
                      <Fragment>
                        <a
                          className="Button Button--white"
                          onClick={closeModal}
                        >
                          {t('common.cancel')}
                        </a>

                        <a
                          className="Button"
                          onClick={async () => {
                            await onJoinGroup(__selectedGroup?.group)

                            __selectedGroup.callback?.()

                            closeModal()
                          }}
                        >
                          {t(
                            'with_group_action.confirm_box.join_private.confirm'
                          )}
                        </a>
                      </Fragment>
                    )
                  }
                  case CONFIRM_BOX_MODE.joinPrivateCancel: {
                    return (
                      <>
                        <a
                          className="Button Button--white"
                          onClick={closeModal}
                        >
                          {t('common.cancel')}
                        </a>

                        <a
                          className="Button Button--red"
                          onClick={async () => {
                            await onLeaveGroup(__selectedGroup?.group)

                            __selectedGroup.callback?.()
                          }}
                        >
                          {t(
                            'with_group_action.confirm_box.join_private_cancel.cancel'
                          )}
                        </a>
                      </>
                    )
                  }
                  case CONFIRM_BOX_MODE.leaveGroup: {
                    return (
                      <Fragment>
                        <a
                          className="Button Button--white"
                          onClick={closeModal}
                        >
                          {t('common.no')}
                        </a>

                        <a
                          className="Button"
                          onClick={async () => {
                            await onLeaveGroup(__selectedGroup?.group)

                            __selectedGroup.callback?.()
                          }}
                        >
                          {t('common.yes')}
                        </a>
                      </Fragment>
                    )
                  }
                  case CONFIRM_BOX_MODE.deleteGroup: {
                    return (
                      <Fragment>
                        <a
                          className="Button Button--white"
                          onClick={closeModal}
                        >
                          {t('common.no')}
                        </a>

                        <a
                          className="Button"
                          onClick={() => {
                            onDeleteGroup(__selectedGroup?.group)
                          }}
                        >
                          {t('common.yes')}
                        </a>
                      </Fragment>
                    )
                  }
                  case CONFIRM_BOX_MODE.groupGuidelines: {
                    return (
                      <Fragment>
                        <a
                          className="Button Button--white"
                          onClick={closeModal}
                        >
                          {t('common.cancel')}
                        </a>

                        <a
                          className="Button"
                          onClick={async () => {
                            await onJoinGroup(__selectedGroup?.group)

                            __selectedGroup?.callback?.()

                            closeModal()
                          }}
                        >
                          {__selectedGroup?.group?.is_private &&
                          !__selectedGroup?.group?.invited_to_join
                            ? t(
                                'with_group_action.confirm_box.join_private_with_guidelines.confirm'
                              )
                            : t(
                                'with_group_action.confirm_box.join_public_with_guidelines.confirm'
                              )}
                        </a>
                      </Fragment>
                    )
                  }
                }
              })()}
            </div>
          </div>
        </ConfirmBox>
      )
    },
  }
}
